document.addEventListener('DOMContentLoaded', () => {
  const DEFAULT_MEASUREMENT_ID = 'G-LJD362EHX6';
  const CUSTOM_ANALYTICS_EVENT = 'hdt:custom-analytics';

  let privacyApiLoadAttempted = false;

  const ensurePrivacyApi = () => {
    if (privacyApiLoadAttempted || !window.Shopify?.loadFeatures) {
      return;
    }

    privacyApiLoadAttempted = true;
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      () => {}
    );
  };

  const getConsentState = () => {
    try {
      if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed !== 'function') {
        ensurePrivacyApi();
        return 'unknown';
      }

      const analyticsAllowed = window.Shopify.customerPrivacy.analyticsProcessingAllowed();
      return analyticsAllowed === true ? 'granted' : analyticsAllowed === false ? 'denied' : 'unknown';
    } catch (error) {
      return 'unknown';
    }
  };

  const sendEvent = (type, additionalParams = {}, options = {}) => {
    if (!type) return;

    const eventDetails = {
      send_to: DEFAULT_MEASUREMENT_ID,
      ...additionalParams
    };

    if (options.useBeacon) {
      eventDetails.transport_type = 'beacon';
    }

    // The CMP owns Google Consent Mode; custom events only read analytics permission.
    const consentState = getConsentState();
    if (consentState === 'granted' && typeof window.gtag === 'function') {
      window.gtag('event', type, eventDetails);
    }

    const customerEventApi = window.Shopify?.analytics || window.analytics;
    if (typeof customerEventApi?.publish === 'function') {
      customerEventApi.publish('emited_click_event', { type, ...additionalParams });
    }
  };

  document.addEventListener('hdt:storefront-prompt:analytics', event => {
    const payload = event?.detail;
    if (!payload || payload.event !== 'storefront_prompt') return;
    const { event: type, ...params } = payload;
    sendEvent(type, params);
  });

  const externalModelEventLabels = {
    external_3d_entry_click: 'External 3D entry clicked',
    external_3d_open: 'External 3D opened',
    external_3d_loaded: 'External 3D loaded',
    external_3d_engaged: 'External 3D engaged',
    external_3d_ar_open: 'External 3D AR opened'
  };

  const externalModelAllowedParams = [
    'product_id',
    'product_handle',
    'variant_id',
    'variant_title',
    'language',
    'market_country',
    'has_usdz',
    'load_time_ms',
    'engagement_type',
    'ar_platform'
  ];

  const normalizeAnalyticsValue = (value) => {
    if (value === null || typeof value === 'undefined') return null;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const normalized = String(value).trim();
    return normalized ? normalized.slice(0, 100) : null;
  };

  const customAnalyticsAllowedParams = {
    shipping_quote_request: ['form_context', 'destination_country', 'cart_item_count'],
    shipping_quote_error: ['form_context', 'error_code'],
    cart_save_for_later: ['save_scope', 'item_count'],
    cart_clear: ['clear_scope'],
    collection_description_expand: ['interaction_source'],
    wishlist_drawer_open: ['interaction_source', 'item_count'],
    wishlist_remove: ['interaction_source', 'item_count'],
    wishlist_clear: ['interaction_source', 'item_count'],
    wishlist_add_list_to_cart: ['interaction_source', 'item_count'],
    wishlist_add_list_error: ['interaction_source', 'error_code'],
    wishlist_full_list_click: ['interaction_source', 'item_count']
  };

  const customAnalyticsEventLabels = {
    shipping_quote_request: 'Request Shipping Quote accepted',
    shipping_quote_error: 'Request Shipping Quote failed',
    cart_save_for_later: 'Cart save for later confirmed',
    cart_clear: 'Cart clear confirmed',
    collection_description_expand: 'Collection description expanded',
    wishlist_drawer_open: 'Wishlist drawer opened',
    wishlist_remove: 'Saved wishlist entry removed',
    wishlist_clear: 'Wishlist cleared',
    wishlist_add_list_to_cart: 'Wishlist list added to cart',
    wishlist_add_list_error: 'Wishlist list cart addition failed',
    wishlist_full_list_click: 'Full wishlist link clicked'
  };

  const getAllowedCustomAnalyticsParams = (type, params = {}) => {
    const allowedParams = customAnalyticsAllowedParams[type];
    if (!allowedParams || !params || typeof params !== 'object' || Array.isArray(params)) return {};

    return allowedParams.reduce((memo, key) => {
      const value = normalizeAnalyticsValue(params[key]);
      if (value !== null) memo[key] = value;
      return memo;
    }, {});
  };

  const getExternalModelGa4Params = (params = {}) => {
    return externalModelAllowedParams.reduce((memo, key) => {
      const value = normalizeAnalyticsValue(params[key]);
      if (value !== null) memo[key] = value;
      return memo;
    }, {});
  };

  const closestElement = (target, selector) => {
    if (!(target instanceof Element)) return null;
    return target.closest(selector);
  };

  const getElementText = (element) => {
    return element?.textContent?.replace(/\s+/g, ' ').trim().slice(0, 100) || null;
  };

  const getPageType = () => {
    const explicitPageType = document.body?.dataset?.template
      || window.ShopifyAnalytics?.meta?.page?.pageType;
    if (explicitPageType) return explicitPageType;

    const classMatch = document.body?.className?.match(/(?:^|\s)(?:template|hdt-page-type)-([a-z0-9_-]+)/i);
    if (classMatch?.[1]) return classMatch[1].replace(/-/g, '_');

    const segments = window.location.pathname.split('/').filter(Boolean);
    const route = segments.find(segment => ['products', 'collections', 'cart', 'search', 'pages', 'blogs'].includes(segment));
    return ({ products: 'product', collections: 'collection', cart: 'cart', search: 'search', pages: 'page', blogs: 'blog' })[route] || null;
  };

  const getProductHandle = () => {
    const segments = window.location.pathname.split('/').filter(Boolean);
    const productsIndex = segments.indexOf('products');
    if (productsIndex === -1 || !segments[productsIndex + 1]) return null;

    try {
      return decodeURIComponent(segments[productsIndex + 1]);
    } catch (error) {
      return segments[productsIndex + 1];
    }
  };

  const getMainVariantId = () => {
    const pickerVariant = document.querySelector('hdt-variant-picker[selected-variant]')?.getAttribute('selected-variant');
    if (pickerVariant) return pickerVariant;

    const mainProductForm = Array.from(document.querySelectorAll('form[action*="/cart/add"]')).find(form => {
      return !form.closest('.accessory-item') && form.querySelector('[name="id"]');
    });
    const formVariant = mainProductForm?.querySelector('[name="id"]')?.value;
    if (formVariant) return formVariant;

    return new URLSearchParams(window.location.search).get('variant')
      || window.ShopifyAnalytics?.meta?.selectedVariantId
      || null;
  };

  const getPageContext = () => ({
    page_type: getPageType(),
    page_path: window.location.pathname
  });

  document.addEventListener(CUSTOM_ANALYTICS_EVENT, event => {
    const detail = event?.detail;
    const type = detail?.type;
    if (!Object.prototype.hasOwnProperty.call(customAnalyticsAllowedParams, type)) return;

    sendEvent(type, {
      event_category: 'engagement',
      event_label: customAnalyticsEventLabels[type],
      ...getPageContext(),
      ...getAllowedCustomAnalyticsParams(type, detail.params)
    }, {
      useBeacon: detail.options?.useBeacon === true
    });
  });

  const getProductContext = (element) => {
    const productRoot = element?.closest?.('[data-product-contact-drawer], .hdt-section--feature-product, product-info, .shopify-section');
    const contactDrawer = element?.closest?.('[data-product-contact-drawer]');
    const productTitle = contactDrawer?.dataset.productTitle
      || productRoot?.querySelector?.('.hdt-product__title, [data-product-title]')?.textContent?.trim()
      || window.ShopifyAnalytics?.meta?.product?.title
      || null;

    return {
      ...getPageContext(),
      product_title: productTitle ? productTitle.slice(0, 100) : null,
      product_handle: window.ShopifyAnalytics?.meta?.product?.handle || getProductHandle(),
      variant_id: getMainVariantId(),
      contact_context: contactDrawer?.dataset.pageType || null
    };
  };

  const getLinkedProductContext = (element) => {
    const linkedRoot = element?.closest?.('.hdt-linked-product');
    const collectionName = linkedRoot?.querySelector?.('.hdt-linked-product__collection-name')?.textContent?.trim()
      || element?.dataset?.collectionName?.trim()
      || null;
    const label = element?.querySelector?.('.hdt-form-color-name, .hdt-linked-product-title')?.textContent?.trim()
      || element?.querySelector?.('img')?.getAttribute('alt')
      || getElementText(element);

    return {
      ...getProductContext(element),
      collection_name: collectionName ? collectionName.slice(0, 100) : null,
      linked_product_label: label ? label.slice(0, 100) : null,
      linked_products_source: linkedRoot?.classList.contains('hdt-linked-product--family-fallback') ? 'family_url_fallback' : 'linked_product_metafield'
    };
  };

  document.addEventListener('hdt:external-model:analytics', (event) => {
    const detail = event.detail || {};
    const type = detail.type;

    if (!externalModelEventLabels[type]) return;

    const ga4Params = {
      event_category: 'engagement',
      event_label: externalModelEventLabels[type],
      ...getExternalModelGa4Params(detail.params)
    };
    const eventOptions = type === 'external_3d_ar_open' ? { useBeacon: true } : {};

    sendEvent(type, ga4Params, eventOptions);
  });

  ensurePrivacyApi();

  document.addEventListener('click', (event) => {
    const target = event.target;
    const externalLink = closestElement(target, 'a[aria-describedby="a11y-new-window-external-message"], a[href^="https://api.whatsapp.com"], a[href*="wa.me"], a[href^="tel:"], a[href^="mailto:"]');

    if (externalLink) {
      const url = externalLink.getAttribute('href');
      let eventDetails = {};

      if (url?.startsWith('tel:')) {
        eventDetails = { event_category: 'engagement', event_label: 'Phone link clicked', link_context: 'generic' };
        sendEvent('phone_link', eventDetails);
      } else if (url?.includes('wa.me') || url?.includes('whatsapp.com')) {
        const hasLegacyProductInquiryButton = Boolean(externalLink.closest('button.product_inquiry_whatsapp'));
        const isProductContactWhatsApp = Boolean(externalLink.closest('[data-product-contact-drawer]')) || externalLink.matches('[data-product-contact-whatsapp]');
        const isProductInquiryWhatsApp = hasLegacyProductInquiryButton
          || isProductContactWhatsApp
          || externalLink.matches('[data-whatsapp-link], .hdt-pr-popup__whatsapp')
          || Boolean(externalLink.closest('[data-whatsapp-link], .hdt-pr-popup__whatsapp, .product_inquiry_whatsapp'));
        eventDetails = isProductInquiryWhatsApp ?
          {
            event_category: 'engagement',
            event_label: isProductContactWhatsApp ? 'Product Contact WhatsApp clicked' : 'Product Inquiry WhatsApp',
            link_context: isProductContactWhatsApp ? 'product_contact_drawer' : 'product_inquiry',
            ...getProductContext(externalLink)
          } :
          { event_category: 'engagement', event_label: 'WhatsApp link clicked', link_context: 'generic', ...getPageContext() };
        sendEvent(isProductContactWhatsApp ? 'product_contact_whatsapp' : (isProductInquiryWhatsApp ? 'product_inquiry_whatsapp' : 'whatsapp_link'), eventDetails);
      } else if (url?.startsWith('mailto:')) {
        eventDetails = { event_category: 'engagement', event_label: 'Email link clicked', link_context: 'generic', ...getPageContext() };
        sendEvent('email_link', eventDetails);
      }
    }

    const clickTrackers = [
      {
        selector: '.hdt-product-contact-launcher',
        event: 'product_contact_open',
        label: 'Product contact drawer opened',
        params: (element) => ({ link_context: 'floating_contact_launcher', ...getProductContext(element) })
      },
      {
        selector: '[data-product-contact-show-form]',
        event: 'product_contact_email_start',
        label: 'Product contact email form opened',
        params: (element) => ({ link_context: 'product_contact_drawer', ...getProductContext(element) })
      },
      {
        selector: '[data-product-contact-back]',
        event: 'product_contact_back',
        label: 'Product contact drawer back clicked',
        params: (element) => ({ link_context: 'product_contact_drawer', ...getProductContext(element) })
      },
      {
        selector: '.hdt-has-btn-size-chart button',
        event: 'finishes_guide',
        label: 'Finishes Guide clicked',
        params: (element) => ({ link_context: 'variant_finish_guide', link_text: getElementText(element), ...getProductContext(element) })
      },
      {
        selector: 'a[href="#ProductDetails"]',
        event: 'product_details_link',
        label: 'Product details link clicked',
        params: (element) => ({ link_context: 'short_summary', link_text: getElementText(element), ...getProductContext(element) })
      },
      {
        selector: '[data-product-detail-trigger]',
        event: 'product_detail_quick_link',
        label: 'Product detail quick link clicked',
        params: (element) => ({ detail_key: element.dataset.productDetailTrigger || null, link_text: getElementText(element), ...getProductContext(element) })
      },
      {
        selector: '.hdt-linked-product__full[href], .hdt-product-collection-drawer__view-all',
        event: 'see_full_collection',
        label: 'See Full Collection clicked',
        params: (element) => ({ link_context: 'linked_products_full_collection', link_text: getElementText(element), ...getLinkedProductContext(element) })
      },
      {
        selector: '.hdt-product__linked_products a[href]:not(.hdt-linked-product__full)',
        event: 'linked_product',
        label: 'Linked Product clicked',
        params: (element) => ({ link_context: 'linked_products_item', ...getLinkedProductContext(element) })
      },
      {
        selector: '.hdt-product__variant-picker .hdt-product-form_value.is-type-color',
        event: 'product_swatch',
        label: 'Product Swatch clicked',
        params: (element) => ({ link_context: 'variant_picker', swatch_label: getElementText(element), ...getProductContext(element) })
      }
    ];

    for (const tracker of clickTrackers) {
      const element = closestElement(target, tracker.selector);
      if (!element) continue;

      sendEvent(tracker.event, {
        event_category: 'engagement',
        event_label: tracker.label,
        ...(typeof tracker.params === 'function' ? tracker.params(element) : {})
      });
      break;
    }
  });

  // Native toggle captures real openings, including quick links and keyboard use.
  document.addEventListener('toggle', (event) => {
    const drawer = event.target;
    if (!(drawer instanceof Element) || !drawer.matches('[data-product-detail-drawer]') || !drawer.open) return;
    sendEvent('product_detail_drawer_open', {
      event_category: 'engagement', event_label: 'Product detail drawer opened',
      detail_key: drawer.dataset.productDetailKey || null,
      ...getProductContext(drawer)
    });
  }, true);

  // Event listener for form submissions
  const getFormValue = (form, selector) => form.querySelector(selector)?.value?.trim() || '';
  const getFormMessage = (form) => getFormValue(form, 'textarea[name="contact[message]"]') || getFormValue(form, 'textarea[name="contact[body]"]');

  const formHandlers = {
    '.hdt-modal-popup__ask form': async (form) => {
      const name = getFormValue(form, 'input[name="contact[name]"]');
      const email = getFormValue(form, 'input[name="contact[email]"]');
      const phone = getFormValue(form, 'input[name="contact[phone]"]');
      if (name && email && phone) {
        sendEvent('product_inquiry_form', {
          event_category: 'engagement',
          event_label: 'Product Inquiry Form submitted'
        });
      }
    },
    '.hdt-product-contact-drawer__form': async (form) => {
      const name = getFormValue(form, 'input[name="contact[name]"]');
      const email = getFormValue(form, 'input[name="contact[email]"]');
      const message = getFormMessage(form);
      if (name && email && message) {
        sendEvent('product_contact_form', {
          event_category: 'engagement',
          event_label: 'Product Contact Form submitted',
          form_context: 'floating_contact_drawer',
          ...getProductContext(form)
        });
      }
    },
    '.hdt-footer-newsletter form, .hdt-modal-newsletter form': async (form) => {
      const email = getFormValue(form, 'input[name="contact[email]"]');
      if (email) {
        sendEvent('newsletter_sign_up', {
          event_category: 'engagement',
          event_label: 'Newsletter Sign Up form submitted'
        });
      }
    },
    '.hdt-form_contact form': async (form) => {
      const name = getFormValue(form, 'input[name="contact[name]"]');
      const email = getFormValue(form, 'input[name="contact[email]"]');
      const message = getFormMessage(form);
      if (name && email && message) {
        sendEvent('contact_form', {
          event_category: 'engagement',
          event_label: 'Contact Form submitted'
        });
      }
    }
  };

  Object.keys(formHandlers).forEach(selector => {
    document.querySelectorAll(selector).forEach(form => {
      form.addEventListener('submit', () => {
        const handler = formHandlers[selector];
        if (typeof handler === 'function') {
          Promise.resolve()
            .then(() => handler(form))
            .catch(error => {
              console.error('Error executing GA4 form handler', error);
            });
        }
      });
    });
  });
});
